import { z } from 'zod';

import type { QuestionTypeValue } from '@/constants/QuestionType';
import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';
import type { ReviewQuestionnaire } from '@/types/review/ReviewQuestionnaire';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { QuestionType, questionTypeValues } from '@/constants/QuestionType';
import { ManagerAssessmentSkillSelectorOption } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { getTextByLanguage, type LocalizedText } from '@/utils/localization';
import { filterOutOtherOption } from '@/utils/question';

const questionLabelRequired: TranslationKey =
    'evaluation360Questionnaire.validation.questionLabelRequired';
const questionLabelMin: TranslationKey = 'evaluation360Questionnaire.validation.questionLabelMin';
const optionLabelRequired: TranslationKey =
    'evaluation360Questionnaire.validation.optionLabelRequired';
const checkboxMinOptions: TranslationKey =
    'evaluation360Questionnaire.validation.checkboxMinOptions';
const questionnaireTitleMin: TranslationKey =
    'evaluation360Questionnaire.validation.questionnaireTitleMin';
const maximumChoicesRequired: TranslationKey =
    'evaluation360Questionnaire.validation.maximumChoicesRequired';
const maximumChoicesMin: TranslationKey = 'evaluation360Questionnaire.validation.maximumChoicesMin';
const maximumChoicesMax: TranslationKey = 'evaluation360Questionnaire.validation.maximumChoicesMax';
const ratingScaleExactOptions: TranslationKey =
    'evaluation360Questionnaire.validation.ratingScaleExactOptions';
const ratingScaleOrder: TranslationKey = 'evaluation360Questionnaire.validation.ratingScaleOrder';
const multipleChoiceMinOptions: TranslationKey =
    'evaluation360Questionnaire.validation.multipleChoiceMinOptions';
const rankingMinOptions: TranslationKey = 'evaluation360Questionnaire.validation.rankingMinOptions';
const managerSkillSelectorRequired: TranslationKey =
    'evaluation360Questionnaire.validation.managerSkillSelectorRequired';
const noQuestionsMessage: TranslationKey = 'evaluation360Questionnaire.message.noQuestions';

const optionSchema = z.object({
    id: z.string(),
    reviewQuestionId: z.string().optional(),
    sortOrder: z.number(),
    label: z.record(z.string(), z.string()).refine(
        (record) => {
            const values = Object.values(record);
            return values.length > 0 && values.some((val) => val.trim().length > 0);
        },
        { message: optionLabelRequired },
    ),
    scoreValue: z.number(),
    isSystemGenerated: z.boolean().optional(),
}) satisfies z.ZodType<ReviewQuestionOption>;

const questionSchema = z
    .object({
        id: z.string().optional(),
        label: z
            .record(z.string(), z.string())
            .refine(
                (record) => {
                    const values = Object.values(record);
                    return values.length > 0 && values.some((val) => val.trim().length > 0);
                },
                { message: questionLabelRequired },
            )
            .refine(
                (record) => {
                    const values = Object.values(record);
                    return values.some((val) => val.trim().length >= 3);
                },
                { message: questionLabelMin },
            ),
        questionType: z.enum(questionTypeValues as [QuestionTypeValue, ...QuestionTypeValue[]]),
        reviewQuestionOptions: optionSchema.array().optional(),
        usefulTip: z.record(z.string(), z.string()).optional(),
        sortOrder: z.number().optional(),
        isAnswerMandatory: z.boolean().optional(),
        isCommentAllowed: z.boolean().optional(),
        isIncludedInBestOrWorstQuestions: z.boolean().optional(),
        isIncludedInScoreCalculation: z.boolean().optional(),
        reviewQuestionnaireId: z.string().nullish(),
        reviewTemplateId: z.string().nullish(),
        reviewQuestionId: z.string().optional(),
        cycleQuestionnaireId: z.string().nullish(),
        competencyQuestionnaireId: z.string().nullish(),
        companyValueId: z.string().nullish(),
        status: z.enum([ReviewQuestionStatus.ACTIVE, ReviewQuestionStatus.INACTIVE]).optional(),
        hasOtherOption: z.boolean().optional(),
        maximumChoices: z.number().nullish(),
    })
    .refine(
        (data) => {
            if (data.questionType === QuestionType.CHECKBOX) {
                return (data.reviewQuestionOptions?.length ?? 0) >= 2;
            }
            return true;
        },
        {
            message: checkboxMinOptions,
            path: ['reviewQuestionOptions'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType === QuestionType.MULTI_CHOICE) {
                return filterOutOtherOption(data.reviewQuestionOptions).length >= 2;
            }
            return true;
        },
        {
            message: multipleChoiceMinOptions,
            path: ['reviewQuestionOptions'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType === QuestionType.RANKING) {
                return (data.reviewQuestionOptions?.length ?? 0) >= 2;
            }
            return true;
        },
        {
            message: rankingMinOptions,
            path: ['reviewQuestionOptions'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType === QuestionType.CHECKBOX) {
                return data.maximumChoices != null;
            }
            return true;
        },
        {
            message: maximumChoicesRequired,
            path: ['maximumChoices'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType === QuestionType.CHECKBOX && data.maximumChoices != null) {
                return data.maximumChoices >= 1;
            }
            return true;
        },
        {
            message: maximumChoicesMin,
            path: ['maximumChoices'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType === QuestionType.CHECKBOX && data.maximumChoices != null) {
                // Count regular options (excluding "Other" option if it exists in the array)
                const regularOptions =
                    data.reviewQuestionOptions?.filter((option) => !option.isSystemGenerated) ?? [];

                // Add 1 if hasOtherOption is checked (for new questions or existing ones)
                const totalOptions = regularOptions.length + (data.hasOtherOption ? 1 : 0);

                return data.maximumChoices <= totalOptions;
            }
            return true;
        },
        {
            message: maximumChoicesMax,
            path: ['maximumChoices'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType !== QuestionType.RATING_SCALE) return true;
            const options = data.reviewQuestionOptions ?? [];
            return options.length === 2;
        },
        {
            message: ratingScaleExactOptions,
            path: ['reviewQuestionOptions'],
        },
    )
    .refine(
        (data) => {
            if (data.questionType !== QuestionType.RATING_SCALE) return true;
            const options = data.reviewQuestionOptions ?? [];
            if (options.length !== 2) return true;

            const [fromOption, toOption] = [...options].sort((a, b) => a.sortOrder - b.sortOrder);

            return fromOption.scoreValue < toOption.scoreValue;
        },
        {
            message: ratingScaleOrder,
            path: ['reviewQuestionOptions'],
        },
    ) satisfies z.ZodType<ReviewQuestion>;

export const createQuestionnaireSchema = (selectedLanguage: string) =>
    z.object({
        id: z.string(),
        title: z
            .record(z.string(), z.string())
            .refine((record) => getTextByLanguage(record, selectedLanguage).trim().length >= 3, {
                message: questionnaireTitleMin,
            }),

        reviewTemplateId: z.string().nullable(),
        reviewQuestions: questionSchema.array(),
    }) satisfies z.ZodType<ReviewQuestionnaire>;

export const createCompetencyQuestionnaireSchema = () =>
    z.object({
        id: z.string(),
        reviewQuestions: questionSchema.array(),
    });

export const createReviewTemplateSchema = (selectedLanguage: string) =>
    z
        .object({
            id: z.string(),
            title: z
                .record(z.string(), z.string())
                .refine(
                    (record) => getTextByLanguage(record, selectedLanguage).trim().length >= 3,
                    {
                        message: questionnaireTitleMin,
                    },
                ),
            reviewType: z.enum([
                EvaluationType.EVALUATION_360,
                EvaluationType.SELF_EVALUATION,
                EvaluationType.MANAGER_EVALUATION,
            ]),
            skillSelectorOption: z
                .enum([
                    ManagerAssessmentSkillSelectorOption.ALL_SKILLS,
                    ManagerAssessmentSkillSelectorOption.WEAKEST_SKILLS,
                    ManagerAssessmentSkillSelectorOption.OLDER_THAN_SIX_MONTHS,
                    ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS,
                ])
                .nullish(),
            questions: questionSchema.array(),
        })
        .superRefine((data, ctx) => {
            if (
                data.reviewType === EvaluationType.MANAGER_EVALUATION &&
                !data.skillSelectorOption
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['skillSelectorOption'],
                    message: managerSkillSelectorRequired,
                });
            }

            if (
                data.reviewType === EvaluationType.MANAGER_EVALUATION &&
                data.skillSelectorOption ===
                    ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS &&
                !data.questions.some((q) => q.status === ReviewQuestionStatus.ACTIVE)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['questions'],
                    message: noQuestionsMessage,
                });
            }
        });

export const createTranslateTitleSchema = (targetLanguage: string) =>
    z.object({
        title: z
            .record(z.string(), z.string())
            .refine((record) => (record[targetLanguage] ?? '').trim().length >= 3, {
                message: questionnaireTitleMin,
                path: ['title'],
            }),
    });

export type TranslateTitleFormData = { title: LocalizedText };

export type SingleQuestionFormData = ReviewQuestion;
export type QuestionnaireFormData = ReturnType<typeof createQuestionnaireSchema>['_output'];
export type ReviewTemplateFormData = ReturnType<typeof createReviewTemplateSchema>['_output'];
export type CompetencyQuestionnaireFormData = ReturnType<
    typeof createCompetencyQuestionnaireSchema
>['_output'];
export type ValidatedQuestionnaire = QuestionnaireFormData;
export { questionSchema };
